/**
 * Data layer for `global-goal-tracking` (FS-302).
 *
 * Thin typed fetch wrappers over `/api/v1/global-goal-tracking`. Raw `fetch()`
 * is confined to this file per the dash contract (D6). Every request carries
 * the `x-tenant-id: t1` header for tenant isolation (matches BE `tenant_id`
 * scoping rule).
 *
 * BE shape reminder (mounted at /api/v1/global-goal-tracking):
 *   GET    /?active=1                     -> { goals: NetworkGoalView[] }
 *   POST   /                              -> { goal: NetworkGoalView }     (201)
 *   PATCH  /:id                           -> { goal: NetworkGoalView }
 *   DELETE /:id                           -> { deleted: true, id: string }
 *   POST   /:id/contributions             -> { contribution: ContributionView }
 *   GET    /:id/progress?at=&top=&buckets -> ProgressSummary
 */

// ---------------------------------------------------------------------------
// Shared types — mirror the BE view shapes exactly.
// ---------------------------------------------------------------------------

export const GOAL_METRICS = ["revenue_cents", "conversions"] as const;
export type NetworkGoalMetric = (typeof GOAL_METRICS)[number];

export type NetworkGoalView = {
	id: string;
	name: string;
	metric: NetworkGoalMetric;
	target_value: number;
	period_start_at: number;
	period_end_at: number;
	active: boolean;
	created_at: number;
	updated_at: number;
	deleted_at: number | null;
};

export type ListGoalsResponse = { goals: NetworkGoalView[] };
export type CreateGoalResponse = { goal: NetworkGoalView };
export type DeleteGoalResponse = { deleted: boolean; id: string };

export type CreateGoalInput = {
	name: string;
	metric: NetworkGoalMetric;
	target_value: number;
	period_start_at: number;
	period_end_at: number;
	active?: boolean;
};

// Trackier-style table row — BE drives the real fields; the "type" column in
// the table below reads `metric` and we compute a tracking-pct client-side
// when a progress snapshot is absent.
export type GlobalGoalTrackingRow = NetworkGoalView;

// ---------------------------------------------------------------------------
// Request helpers.
// ---------------------------------------------------------------------------

const BASE = "/api/v1/global-goal-tracking";
const TENANT_HEADER = { "x-tenant-id": "t1" } as const;

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

// ---------------------------------------------------------------------------
// Public calls.
// ---------------------------------------------------------------------------

export async function list(params?: {
	activeOnly?: boolean;
}): Promise<ListGoalsResponse> {
	const qs =
		params?.activeOnly === true ? "?active=1" : "";
	const res = await fetch(`${BASE}${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json", ...TENANT_HEADER },
	});
	return parseOrThrow<ListGoalsResponse>(res);
}

export async function create(
	body: CreateGoalInput,
): Promise<CreateGoalResponse> {
	const payload: Record<string, unknown> = {
		name: body.name,
		metric: body.metric,
		target_value: body.target_value,
		period_start_at: body.period_start_at,
		period_end_at: body.period_end_at,
	};
	if (body.active !== undefined) payload.active = body.active;
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
			...TENANT_HEADER,
		},
		body: JSON.stringify(payload),
	});
	return parseOrThrow<CreateGoalResponse>(res);
}

export async function remove(id: string): Promise<DeleteGoalResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json", ...TENANT_HEADER },
	});
	return parseOrThrow<DeleteGoalResponse>(res);
}

// ---------------------------------------------------------------------------
// Display helpers — pure, no fetch.
// ---------------------------------------------------------------------------

export function metricLabel(metric: NetworkGoalMetric): string {
	return metric === "revenue_cents" ? "Revenue" : "Conversions";
}

export function formatTarget(goal: NetworkGoalView): string {
	if (goal.metric === "revenue_cents") {
		const dollars = goal.target_value / 100;
		return `$${dollars.toLocaleString(undefined, {
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		})}`;
	}
	return goal.target_value.toLocaleString();
}

export function statusLabel(goal: NetworkGoalView): "Active" | "Paused" {
	return goal.active ? "Active" : "Paused";
}

export function elapsedPct(goal: NetworkGoalView, nowMs: number): number {
	const span = goal.period_end_at - goal.period_start_at;
	if (span <= 0) return 0;
	const elapsed = Math.min(
		Math.max(nowMs - goal.period_start_at, 0),
		span,
	);
	return (elapsed / span) * 100;
}

// Validators shared with the create-goal form.
export const NAME_MIN = 1;
export const NAME_MAX = 160;
export const MAX_TARGET_VALUE = 1_000_000_000_000_000;

// ---------------------------------------------------------------------------
// Query keys.
// ---------------------------------------------------------------------------

export const globalGoalTrackingQueryKeys = {
	all: ["global-goal-tracking"] as const,
	list: (activeOnly?: boolean) =>
		[
			...globalGoalTrackingQueryKeys.all,
			{ activeOnly: activeOnly === true },
		] as const,
	detail: (id: string) =>
		[...globalGoalTrackingQueryKeys.all, id] as const,
};
